import { Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loadable, ToastMessage } from '../../../../common';
import {
    Portlet, PortletBody,
    PortletHeader, PortletHeaderToolbar
} from "../../../../partials/content/Portlet";

import {
    Button,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    TextField,
    Tooltip,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    MenuItem,
    Autocomplete,
    Checkbox,
    Chip,
    Stack,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import DeleteIcon from '@mui/icons-material/Delete';
import { BackButton } from "../../../../common/BackButton";
import { ConfigurationClearConfirmationDialog } from "../ClientsConfigurationClearConfirmationDialog";
import { AddClientsDialog } from "../ClientsDialog";
import { errorToastify, infoToastify } from "../../../../util/toast";
import { FHIRProviderType } from "../../../../store/constants/pages";
import { toast } from "react-toastify";
import { useData } from "@pure-ptr/react";
import { generateStats } from "../../../../store/api/pricers";
import AddCircleIcon from '@mui/icons-material/AddCircle';

const useStyles = makeStyles(theme => ({
    deleteButton: {
        color: '#d9534f',
        marginLeft: '20px',
    },
    button: {
        margin: theme.spacing(1),
    },

    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '80%',
    },
    textArea: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '80%',
    },
    switch: {
        margin: theme.spacing(1),
    },
    switchAccount: {
        margin: 0,
    },
    switchAccountFormLabel: {

        position: 'absolute',
        bottom: 0
    },
    margin: {
        margin: theme.spacing(1),
    },

    formControl: {
        margin: theme.spacing(1),
        width: '80%',
    },
    updatedSection: {
        display: 'block'
    },
    approveIcon: {
        position: 'absolute',
        bottom: '10px'
    },
    updatedSectionContainer: {
        marginLeft: '8px'
    },
    loadable: {
        background: "white"
    },
    boxPadding: {
        padding: "0 40px 0 40px"
    },
    deleteIconButton: {
        color: '#d9534f',
    },

}));

const headerBgColor = { background: "#f7f8fa" }

export default function ClientConfigurationPage({
    intl, initialValues, initialValuesAccount, initialValuesFHIR, initialPayerIntegrations, payerContractOptions, initialValuesClaimNotes, clientId, configuration, isEdit, client, updateAccountPending, updateFHIRPending,
    updateClaimNotesPending, isAccountEdit, isFHIREdit, isClaimNotesEdit, history,
    getClientPending, getClientAccountPending, getClientFHIRPending, getClientClaimNotesPending, createClientPending, createClientAccountPending, createClientFHIRPending, createClientClaimNotesPending, testFHIRConnectionPending, testClaimNotesConnectionPending, updateClientPending, deleteClientPending, isSuperAdmin,
    externalPayers,
    addUpdatePayerIntegrationsPending,
    getPayerIntegrationConfigurations,
    actions: {
        getAccountConfiguration,
        getFHIRConfiguration,
        getClaimNotesConfiguration,
        getConfiguration,
        createConfiguration,
        createAccountConfiguration,
        createFHIRConfiguration,
        createClaimNotesConfiguration,
        testFHIRConnection,
        testClaimNotesConnection,
        updateConfiguration,
        updateAccountConfiguration,
        updateFHIRConfiguration,
        updateClaimNotesConfiguration,
        deleteConfiguration,
        resetClientConfiguration,
        resetClientAccountConfiguration,
        resetClientFHIRConfiguration,
        resetClientClaimNotesConfiguration,
        getClient,
        getPayerContractsTiny,
        getExternalPayers,
        getExternalPayerConfigurations,
        AddUpdatePayerIntegrationConfigurations,
        resetExternalPayerConfigurations
    } }) {

    const classes = useStyles();

    const [open, setOpen] = useState(false)
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
    const saveSectionRef = useRef("");
    const [submitGenerateStats, setSubmitGenerateStats] = useState(false);

    useEffect(() => {
        getConfiguration(clientId, isSuperAdmin);
        getAccountConfiguration(clientId);
        getFHIRConfiguration(clientId);
        getClaimNotesConfiguration(clientId)
        return () => {
            resetClientConfiguration();
            resetClientAccountConfiguration();
            resetClientFHIRConfiguration();
            resetClientClaimNotesConfiguration()
        }
    }, [clientId, getConfiguration, resetClientConfiguration, getAccountConfiguration, getFHIRConfiguration, getClaimNotesConfiguration, resetClientClaimNotesConfiguration, resetClientFHIRConfiguration, resetClientAccountConfiguration]);

    useEffect(() => {
        getClient(clientId);
    }, [getClient, clientId]);

    useEffect(() => {
        getPayerContractsTiny();
        getExternalPayers();
        getExternalPayerConfigurations(clientId);
        return () => {
            resetExternalPayerConfigurations();
        }
    }, [getPayerContractsTiny, getExternalPayers, getExternalPayerConfigurations]);
    const payerOptions = useMemo(() => {
        if (!Array.isArray(externalPayers)) return [];
        return externalPayers
            .map((p: any) => ({
                id: String(p?.id ?? ''),
                name: String(p?.name ?? ''),
                type: p?.type,
            }))
            .filter((p: any) => p.id && p.name);
    }, [externalPayers]);

    const payerById = useMemo(() => {
        return new Map(payerOptions.map((p: any) => [String(p.id), p]));
    }, [payerOptions]);

    const {
        error: generateStatsError,
        isPending: generateStatsPending,
        reload: generateRates
    } = useData(async () => {
        if (!submitGenerateStats) return;
        setSubmitGenerateStats(false);
        await generateStats(clientId);
    }, [clientId]);

    useEffect(() => {
        if (!generateStatsError) return;

        errorToastify(generateStatsError?.payload?.error);
    }, [generateStatsError]);

    const handleCallback = () => {
        getConfiguration(clientId, isSuperAdmin)
        history.push('/admin/clients?persist=true');
    }

    const handleSubmitGenerateStats = () => {
        setSubmitGenerateStats(true);
        infoToastify("Stats generation is in progress. You will be notified once it is complete.");
        generateRates();
    }

    const handleTestCallback = () => {
        toast.success(
            "Connection Successful.",
            {
                autoClose: 3000,
                pauseOnHover: true,
                closeOnClick: true,
            }
        );
    }

    const handleTestConnetion = (config) => {
        const payload = {
            ProviderClientId: config.providerClientId,
            ProviderClientSecret: config.providerClientSecret,
            ValidationType: "FHIR"
        }
        testFHIRConnection(payload, handleTestCallback);
    }

    const handleClaimNotesTestConnection = (config) => {
        const payload = {
            ProviderClientId: config.providerClientId,
            ProviderClientSecret: config.providerClientSecret,
             ValidationType: "ClaimNotes"
        }
        testClaimNotesConnection(payload, handleTestCallback);
    }

    const disabled = (data, dirty) => {
        return !data.providerClientId || !data.providerClientSecret || !dirty;
    }


    const handleDialogOpen = () => { setOpen(true) }
    const handleDialogClose = () => { setOpen(false) }
    const handleConfirmationDialogOpen = () => { setOpenConfirmationDialog(true) }
    const handleConfirmationDialogClose = () => { setOpenConfirmationDialog(false) }

    const handleDeleteConfiguration = () => {
        handleConfirmationDialogClose()
        deleteConfiguration(clientId, handleCallback)
    }

    const isOptumOrUhc = (payerId) => {
        const payer = payerById.get(String(payerId));
        return payer?.type === 0;
    };

    const createEmptyIntegration = () => ({
    uiId: Date.now() + Math.random(),
    payerId: "",
    contracts: []
    });

    const mapPayerIntegrationsToApi = (payerIntegrations: any[]) => {
        return payerIntegrations.map(pi => ({
            ExternalPayerId: Number(pi.payerId),
            Contracts: (pi.contracts || []).map((c: any) => ({
                PayerContractId: c.payerContractId,
                PayerId: c.payerId || ''
            }))
        }));
    };

    const validatePayerIntegrations = (values: any, intl: any, isOptumOrUhc: (id: string) => boolean) => {
    const errors: any = {};
    const integrationErrors: any[] = [];
    let hasErrors = false;

    // map of payerId -> indices to detect duplicates
    const payerIdIndexMap = new Map<string, number[]>();
    (values.payerIntegrations || []).forEach((integration: any, idx: number) => {
        if (integration.payerId) {
            if (!payerIdIndexMap.has(integration.payerId)) {
                payerIdIndexMap.set(integration.payerId, []);
            }
            payerIdIndexMap.get(integration.payerId)!.push(idx);
        }
    });

    (values.payerIntegrations || []).forEach((integration: any, idx: number) => {
        const ie: any = {};

        // Required: External Payer
        if (!integration.payerId) {
            ie.payerId = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
        }

        // Duplicate payer check
        if (integration.payerId) {
            const duplicateIndices = payerIdIndexMap.get(integration.payerId);
            if (duplicateIndices && duplicateIndices.length > 1) {
                ie.payerId = "An integration with this payer already exists.";
            }
        }

        // Required: At least one contract
        const hasContracts = integration.contracts && integration.contracts.length > 0;
        if (!hasContracts) {
            ie.contracts = "At least one payer contract is required.";
        }

        // If Optum/UHC subsection is visible, each contract must have a payerId
        const isOptum = isOptumOrUhc(integration.payerId);
        if (isOptum && hasContracts) {
            const contractErrors: any[] = [];
            let hasContractErrors = false;

            integration.contracts.forEach((contract: any, cIdx: number) => {
                const ce: any = {};
                if (!contract.payerId || !contract.payerId.trim()) {
                    ce.payerId = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                    hasContractErrors = true;
                }
                contractErrors[cIdx] = ce;
            });

            if (hasContractErrors) {
                ie.contracts = contractErrors;
            }
        }

        if (Object.keys(ie).length > 0) hasErrors = true;
        integrationErrors[idx] = ie;
    });

    if (hasErrors) errors.payerIntegrations = integrationErrors;
    return errors;
};

    return (<>
        <Loadable loading={getClientPending || createClientPending || updateClientPending || deleteClientPending} parentClassName={classes.loadable}>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validate={values => {
                    const errors = {};

                    if (values.eapgEnabled) {
                        if (!values.siteName) {
                            errors["siteName"] = intl.formatMessage({
                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                            });
                        } else if (!/^[a-zA-Z0-9 ]+$/.test(values.siteName)) {
                            errors["siteName"] = "Only letters, digits, and spaces allowed";
                        } else if (values.siteName.length < 5) {
                            errors["siteName"] = "Minimum 5 characters";
                        } else if (values.siteName.length > 100) {
                            errors["siteName"] = "Maximum 100 characters";
                        }
                    }


                    return errors;
                }}
                onSubmit={(values, { }) => {
                    const saveSection = saveSectionRef.current;
                    let payload = {
                        ...initialValues,
                        clientId: clientId,
                    };

                    if (saveSection === 'product') {
                        payload = {
                            ...payload,
                            customClaimStatusEnabled: values.customClaimStatusEnabled,
                            customRCMTeamAssignmentEnabled: values.customRCMTeamAssignmentEnabled,
                            summaryDashboardEnabled: values.summaryDashboardEnabled,
                            usePlanIdMapping: values.usePlanIdMapping,
                            eapgEnabled: values.eapgEnabled,
                            remitExtAssignerDedupEnabled: values.remitExtAssignerDedupEnabled,
                            siteName: values.eapgEnabled ? values.siteName?.trim() : initialValues.siteName,
                            isEDIHistoricalStatsEnabled: values.isEDIHistoricalStatsEnabled,
                            percentOfChargeStatsEnabled: values.percentOfChargeStatsEnabled,
                        };
                    } else if (saveSection === 'edi') {
                        payload = {
                            ...payload,
                            prefixExcluded: values.prefixExcluded,
                            postfixExcluded: values.postfixExcluded,
                            fileExtensionsExcluded: values.fileExtensionsExcluded,
                            notifyOnFileNotReceived: values.notifyOnFileNotReceived,
                            notifyAfterDaysFileNotReceived: values.notifyAfterDaysFileNotReceived,
                        };
                    } else if (saveSection === 'ai') {
                        payload = {
                            ...payload,
                            claimAnalysisChatbotEnabled: values.claimAnalysisChatbotEnabled,
                            contractDocumentChatbotEnabled: values.contractDocumentChatbotEnabled,
                            supplementaryKnowledgeBaseEnabled: values.supplementaryKnowledgeableEnabled,
                            supplementaryPromptEnabled: values.supplementaryPromptEnabled,
                            claimSemanticSearchEnabled: values.claimSemanticSearchEnabled,
                            claimAnalysisFollowupEnabled: values.claimAnalysisChatbotEnabled ? values.claimAnalysisFollowupEnabled : false,
                            contractFollowupEnabled: values.contractDocumentChatbotEnabled ? values.contractFollowupEnabled : false,
                            aiEnabled: values.claimAnalysisChatbotEnabled || values.contractDocumentChatbotEnabled || values.supplementaryKnowledgeableEnabled || values.supplementaryPromptEnabled || values.claimSemanticSearchEnabled,
                        };
                    } else {
                        // Fallback to original behavior if no section is specified (should not happen with separate buttons)
                        payload = {
                            ...payload,
                            prefixExcluded: values.prefixExcluded,
                            postfixExcluded: values.postfixExcluded,
                            fileExtensionsExcluded: values.fileExtensionsExcluded,
                            notifyOnFileNotReceived: values.notifyOnFileNotReceived,
                            notifyAfterDaysFileNotReceived: values.notifyAfterDaysFileNotReceived,
                            customClaimStatusEnabled: values.customClaimStatusEnabled,
                            summaryDashboardEnabled: values.summaryDashboardEnabled,
                            usePlanIdMapping: values.usePlanIdMapping,
                            eapgEnabled: values.eapgEnabled,
                            isEDIHistoricalStatsEnabled: values.isEDIHistoricalStatsEnabled,
                            percentOfChargeStatsEnabled: values.percentOfChargeStatsEnabled,
                            aiEnabled: values.claimAnalysisChatbotEnabled || values.contractDocumentChatbotEnabled || values.supplementaryKnowledgeableEnabled || values.supplementaryPromptEnabled || values.claimSemanticSearchEnabled,
                            claimAnalysisChatbotEnabled: values.claimAnalysisChatbotEnabled,
                            contractDocumentChatbotEnabled: values.contractDocumentChatbotEnabled,
                            supplementaryKnowledgeBaseEnabled: values.supplementaryKnowledgeableEnabled,
                            supplementaryPromptEnabled: values.supplementaryPromptEnabled,
                            claimAnalysisFollowupEnabled: values.claimAnalysisChatbotEnabled ? values.claimAnalysisFollowupEnabled : false,
                            contractFollowupEnabled: values.contractDocumentChatbotEnabled ? values.contractFollowupEnabled : false,
                            claimSemanticSearchEnabled: values.claimSemanticSearchEnabled,
                            customRCMTeamAssignmentEnabled: values?.customRCMTeamAssignmentEnabled,
                            remitExtAssignerDedupEnabled: values.remitExtAssignerDedupEnabled,
                            ...(values?.eapgEnabled ? { siteName: values?.siteName?.trim() } : {})
                        };
                    }

                    if (isEdit) {
                        updateConfiguration(payload, handleCallback)
                    } else {
                        createConfiguration(payload, handleCallback)
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    dirty
                }) => {
                    const productFields = ['customClaimStatusEnabled', 'customRCMTeamAssignmentEnabled', 'summaryDashboardEnabled', 'eapgEnabled', 'siteName', 'usePlanIdMapping', 'remitExtAssignerDedupEnabled', 'isEDIHistoricalStatsEnabled', 'percentOfChargeStatsEnabled'];
                    const ediFields = ['prefixExcluded', 'postfixExcluded', 'fileExtensionsExcluded', 'notifyAfterDaysFileNotReceived', 'notifyOnFileNotReceived'];
                    const aiFields = ['claimAnalysisChatbotEnabled', 'contractDocumentChatbotEnabled', 'supplementaryKnowledgeableEnabled', 'claimSemanticSearchEnabled', 'supplementaryPromptEnabled'];

                    const isProductDirty = productFields.some(f => (values[f] ?? "") !== (initialValues[f] ?? ""));
                    const isEDIDirty = ediFields.some(f => (values[f] ?? "") !== (initialValues[f] ?? ""));
                    const isAIDirty = aiFields.some(f => (values[f] ?? "") !== (initialValues[f] ?? ""));

                    return (
                        <form onSubmit={handleSubmit} noValidate autoComplete="off" className={classes.container}>
                            <Box sx={{ width: '100%' }}>
                                <Portlet>
                                    <PortletHeader
                                        style={{ ...headerBgColor }}
                                        toolbar={
                                            <PortletHeaderToolbar className='w-100'>
                                                <Grid container alignItems="center">
                                                    <Grid item md={6} spacing={2} container alignItems="center" className="pl-3">
                                                        <Typography className="text-dark" variant="h6">{`Configuration - ${client?.name}`}</Typography>
                                                        <Tooltip title="Clear Configuration of this client">
                                                            <Button
                                                                size="small"
                                                                onClick={handleConfirmationDialogOpen}
                                                                className={classes.deleteButton}
                                                                startIcon={<DeleteIcon />}>
                                                                Clear
                                                            </Button>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item md={6} container className="justify-content-end" >
                                                        <BackButton text={dirty ? "Cancel" : "Back"} defaultRoute="/admin/clients?persist=true" />

                                                    </Grid>
                                                </Grid>
                                            </PortletHeaderToolbar>
                                        }
                                    />
                                </Portlet>
                            </Box>


                            {/* EDI Configurations Section */}
                            <Box sx={{ width: '100%' }} >
                                <Portlet>
                                    <PortletHeader
                                        style={{ ...headerBgColor }}
                                        toolbar={
                                            <PortletHeaderToolbar className='w-100'>
                                                <Grid container alignItems="center">
                                                    <Grid item md={6} spacing={2} container alignItems="center" className="pl-3">
                                                        <Typography className="text-dark" variant="h6">EDI Configurations</Typography>
                                                    </Grid>
                                                    <Grid item md={6} container className="justify-content-end">
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            type="submit"
                                                            onClick={() => { saveSectionRef.current = 'edi'; }}
                                                            disabled={createClientPending || updateClientPending || (!isEDIDirty)}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </PortletHeaderToolbar>
                                        }
                                    />
                                    <PortletBody>
                                        <Grid container spacing={2}>
                                            <Grid item md={6}>
                                                <TextField
                                                    multiline
                                                    minRows='1'
                                                    maxRows='8'
                                                    label="Prefix Excluded"
                                                    name="prefixExcluded"
                                                    className={classes.textArea}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.prefixExcluded}
                                                    helperText='e.g. SLICED_;HEALTH_'
                                                    error={Boolean(touched.prefixExcluded && errors.prefixExcluded)}
                                                />
                                            </Grid>
                                            <Grid item md={6}>
                                                <TextField
                                                    multiline
                                                    minRows='1'
                                                    maxRows='8'
                                                    label="Postfix Excluded"
                                                    name="postfixExcluded"
                                                    className={classes.textArea}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.postfixExcluded}
                                                    helperText='e.g. _SLICED;_HEALTH'
                                                    error={Boolean(touched.postfixExcluded && errors.postfixExcluded)}
                                                />
                                            </Grid>
                                            <Grid item md={6}>
                                                <TextField
                                                    multiline
                                                    minRows='1'
                                                    maxRows='8'
                                                    label="File Extensions Excluded"
                                                    name="fileExtensionsExcluded"
                                                    className={classes.textArea}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.fileExtensionsExcluded}
                                                    helperText='e.g. exe;pdf;word'
                                                    error={Boolean(touched.fileExtensionsExcluded && errors.fileExtensionsExcluded)}
                                                />
                                            </Grid>
                                            <Grid item md={6}>
                                                <TextField
                                                    label="Notify After Days File Not Received"
                                                    name="notifyAfterDaysFileNotReceived"
                                                    type="number"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.notifyAfterDaysFileNotReceived}
                                                    helperText={touched.notifyAfterDaysFileNotReceived && errors.notifyAfterDaysFileNotReceived}
                                                    error={Boolean(touched.notifyAfterDaysFileNotReceived && errors.notifyAfterDaysFileNotReceived)}
                                                />
                                            </Grid>
                                            <Grid item md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            name="notifyOnFileNotReceived"
                                                            checked={values.notifyOnFileNotReceived}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Notify On File Not Received"
                                                />
                                            </Grid>
                                        </Grid>
                                    </PortletBody>
                                </Portlet>
                            </Box>


                            {/* Product Configurations Section */}
                            <Box sx={{ width: '100%' }} className={classes.boxPadding} >
                                <Portlet>
                                    <PortletHeader
                                        style={{ ...headerBgColor }}
                                        toolbar={
                                            <PortletHeaderToolbar className='w-100'>
                                                <Grid container alignItems="center">
                                                    <Grid item md={6} spacing={2} container alignItems="center" className="pl-3">
                                                        <Typography className="text-dark" variant="h6">Product Configurations</Typography>
                                                    </Grid>
                                                    <Grid item md={6} container className="justify-content-end">
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            type="submit"
                                                            onClick={() => { saveSectionRef.current = 'product'; }}
                                                            disabled={createClientPending || updateClientPending || (!isProductDirty)}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </PortletHeaderToolbar>
                                        }
                                    />
                                    <PortletBody>
                                        <Grid container spacing={2}>
                                            <Grid item md={4}>
                                                <FormControlLabel
                                                    sx={{ display: "block" }}
                                                    control={
                                                        <Switch
                                                            name="customClaimStatusEnabled"
                                                            checked={values.customClaimStatusEnabled}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Enable Custom Claim Statuses"
                                                />
                                            </Grid>
                                            {isSuperAdmin && (
                                                <Grid item md={4}>
                                                    <FormControlLabel
                                                        sx={{ display: "block" }}
                                                        control={
                                                            <Switch
                                                                name="customRCMTeamAssignmentEnabled"
                                                                checked={values.customRCMTeamAssignmentEnabled}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Enable RCM Teams"
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item md={4}>
                                                <FormControlLabel
                                                    sx={{ display: "block" }}
                                                    control={
                                                        <Switch
                                                            name="summaryDashboardEnabled"
                                                            checked={values.summaryDashboardEnabled}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Show Summary Dashboard"
                                                />
                                            </Grid>
                                            <Grid item md={4}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            name="usePlanIdMapping"
                                                            checked={values.usePlanIdMapping}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            color="primary"
                                                        />
                                                    }
                                                    label='Process Claims Using Payer Mapping'
                                                />
                                            </Grid>
                                               <Grid item md={4}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="remitExtAssignerDedupEnabled"
                                                        checked={values.remitExtAssignerDedupEnabled}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        color="primary"
                                                    />
                                                }
                                                label='Enable Remit Ext Assigner Dedup'
                                            />
                                        </Grid>
                                            {isSuperAdmin && (
                                                <Grid item md={4}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: "30px" }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    name="eapgEnabled"
                                                                    checked={values.eapgEnabled}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Enable EAPG"
                                                        />
                                                        {values.eapgEnabled && (
                                                            <TextField
                                                                label="Site Name"
                                                                required
                                                                name="siteName"
                                                                sx={{ "&.MuiFormControl-root": { width: "300px", margin: 0 } }}
                                                                margin="normal"
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.siteName}
                                                                helperText={touched.siteName && errors.siteName}
                                                                error={Boolean(touched.siteName && errors.siteName)}
                                                            />
                                                        )}
                                                    </Box>
                                                </Grid>
                                            )}
                                            <Grid item md={6}>
                                                <Grid container alignItems="center" spacing={2}>
                                                    <Grid item>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    name="ediBasedPricing"
                                                                    checked={values.isEDIHistoricalStatsEnabled}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    color="primary"
                                                                    disabled={true}
                                                                />
                                                            }
                                                            label="EDI-Based Pricing"
                                                        />
                                                    </Grid>

                                                    <Grid item>
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleSubmitGenerateStats}
                                                            disabled={values.isEDIHistoricalStatsEnabled === false || generateStatsPending !== null}

                                                        >
                                                            {generateStatsPending !== null ? "Generating..." : "Generate Stats"}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        <Grid item md={4}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="percentOfChargeStatsEnabled"
                                                        checked={values.percentOfChargeStatsEnabled}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        color="primary"
                                                    />
                                                }
                                                label="Stats for Percents of Charges"
                                            />
                                        </Grid>
                                        </Grid>
                                </PortletBody>
                            </Portlet>
                            </Box>


                            {/* AI Capabilities Section */}
                            <Box sx={{ width: '100%' }} className={classes.boxPadding}>
                                <Portlet>
                                    <PortletHeader
                                        style={{ ...headerBgColor }}
                                        toolbar={
                                            <PortletHeaderToolbar className='w-100'>
                                                <Grid container alignItems="center">
                                                    <Grid item md={6} spacing={2} container alignItems="center" className="pl-3">
                                                        <Typography className="text-dark" variant="h6">AI Capabilities</Typography>
                                                    </Grid>
                                                    <Grid item md={6} container className="justify-content-end">
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            type="submit"
                                                            onClick={() => { saveSectionRef.current = 'ai'; }}
                                                            disabled={createClientPending || updateClientPending || (!isAIDirty)}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </PortletHeaderToolbar>
                                        }
                                    />
                                    <PortletBody>
                                        {isSuperAdmin && (
                                            <Grid container spacing={2}>
                                                <Grid item md={4}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="claimAnalysisChatbotEnabled"
                                                                color="primary"
                                                                checked={values.claimAnalysisChatbotEnabled}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                        }
                                                        label="Claim Analysis Chatbot"
                                                    />
                                                </Grid>
                                                <Grid item md={4}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="contractDocumentChatbotEnabled"
                                                                color="primary"
                                                                checked={values.contractDocumentChatbotEnabled}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                        }
                                                        label="Contract Document Chatbot"
                                                    />
                                                </Grid>
                                                <Grid item md={4}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="supplementaryKnowledgeableEnabled"
                                                                color="primary"
                                                                checked={values.supplementaryKnowledgeableEnabled}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                        }
                                                        label="IQ Knowledge Base"
                                                    />
                                                </Grid>
                                                <Grid item md={4}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="claimSemanticSearchEnabled"
                                                                color="primary"
                                                                checked={values.claimSemanticSearchEnabled}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                        }
                                                        label="Claim Semantic Search"
                                                    />
                                                </Grid>
                                                <Grid item md={4}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="supplementaryPromptEnabled"
                                                                color="primary"
                                                                checked={values.supplementaryPromptEnabled}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                        }
                                                        label="Supplementary Prompts"
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                    </PortletBody>
                                </Portlet>
                            </Box>
                        </form>
                    );
                }}
            </Formik>
        </Loadable>
        {/* Account Section  */}
        <Loadable loading={updateAccountPending || createClientAccountPending || getClientAccountPending}>
            <Formik
                enableReinitialize={true}
                initialValues={initialValuesAccount}
                validate={values => {

                    const errors: any = {};

                    if (!values.accountIdentifierNumbers) {
                        errors.accountIdentifierNumbers = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    return errors;
                }}
                onSubmit={(values, { }) => {

                    let payload = {
                        accountIdentifierNumbers: values?.accountIdentifierNumbers,
                        mergeClaims: values?.mergeClaims,
                        clientId: clientId,
                    }

                    if (isAccountEdit) {
                        updateAccountConfiguration(payload, handleCallback)
                    }
                    else {
                        createAccountConfiguration(payload, handleCallback)
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    dirty
                }) => (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off" className={classes.container}>
                        <Portlet>
                            <PortletHeader
                                style={{ ...headerBgColor }}
                                toolbar={
                                    <PortletHeaderToolbar className='w-100'>
                                        <Grid container alignItems="center">
                                            <Grid item md={6} spacing={2} container alignItems="center" className="pl-3">
                                                <Typography className="text-dark" variant="h6">Account</Typography>


                                            </Grid>
                                            <Grid item md={6} container className="justify-content-end">
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className='ml-3'
                                                    type="submit"
                                                    disabled={!dirty}
                                                >
                                                    Save
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </PortletHeaderToolbar>
                                }
                            />
                            <PortletBody>
                                <div className={classes.container}>
                                    <div className="row" style={{ width: '90%' }}>



                                        <div className="col-md-6">
                                            <TextField
                                                type="number"
                                                required
                                                label="Account digit"
                                                name="accountIdentifierNumbers"
                                                className={classes.textField}

                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.accountIdentifierNumbers}
                                                helperText={
                                                    touched.accountIdentifierNumbers && errors.accountIdentifierNumbers
                                                }
                                                error={Boolean(
                                                    touched.accountIdentifierNumbers && errors.accountIdentifierNumbers
                                                )}
                                            />


                                            {initialValuesAccount?.updatedAt &&
                                                <Tooltip title="Validate Claims">
                                                    <IconButton size='small' onClick={handleDialogOpen} className={classes.approveIcon}>
                                                        <CheckCircleOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            }



                                        </div>

                                        <div className="col-md-6">
                                            <FormControlLabel
                                                className={classes.switchAccountFormLabel}
                                                control={
                                                    <Switch
                                                        name="mergeClaims"
                                                        checked={values.mergeClaims}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        color="primary"
                                                    />
                                                }
                                                label="Merge claims by account id."
                                            />
                                        </div>



                                    </div>

                                </div>
                                {initialValuesAccount?.updatedAt &&
                                    <div className={classes.updatedSectionContainer}>
                                        <span className={classes.updatedSection}>
                                            <b>Updated At :</b> {initialValuesAccount?.updatedAt}
                                        </span>

                                        <span className={classes.updatedSection}>
                                            <b>Updated by :</b> &nbsp;{initialValuesAccount?.updatedBy}
                                        </span>
                                    </div>
                                }

                            </PortletBody>
                        </Portlet>
                    </form>
                )}
            </Formik>
        </Loadable>

        {/* FHIR Section  */}
        <Loadable loading={updateFHIRPending || createClientFHIRPending || getClientFHIRPending || testFHIRConnectionPending}>
            <Formik
                enableReinitialize={true}
                initialValues={initialValuesFHIR}
                validate={values => {
                    const errors: any = {};

                    if (values.providerType != 0 && values.providerType != 1) {
                        errors.providerType = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (!values.practiceId) {
                        errors.practiceId = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (!values.providerClientSecret) {
                        errors.providerClientSecret = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (!values.providerClientId) {
                        errors.providerClientId = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    return errors;
                }}
                onSubmit={(values, { }) => {
                    let payload = {
                        providerType: values?.providerType,
                        practiceId: values?.practiceId,
                        providerClientSecret: values?.providerClientSecret,
                        providerClientId: values?.providerClientId,
                        isPatientDetailsEnabled: values?.isPatientDetailsEnabled,
                        clientId
                    }

                    if (isFHIREdit) {
                        updateFHIRConfiguration(payload, handleCallback)
                    }
                    else {
                        createFHIRConfiguration(payload, handleCallback)
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    dirty
                }) => (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off" className={classes.container}>
                        <Portlet>
                            <PortletHeader
                                style={{ ...headerBgColor }}
                                toolbar={
                                    <PortletHeaderToolbar className='w-100'>
                                        <Grid container alignItems="center">
                                            <Grid item md={6} spacing={2} container alignItems="center" className="pl-3">
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={3}
                                                >
                                                    <Typography className="text-dark" variant="h6">FHIR</Typography>
                                                    <Switch
                                                        name="isPatientDetailsEnabled"
                                                        checked={values.isPatientDetailsEnabled}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        color="primary"
                                                    />
                                                </Stack>
                                            </Grid>
                                            <Grid item md={6} container className="justify-content-end">
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className='ml-3'
                                                    onClick={() => handleTestConnetion(values)}
                                                    disabled={!values.isPatientDetailsEnabled || disabled(values, dirty)}
                                                >
                                                    Test Credentials
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className='ml-3'
                                                    type="submit"
                                                    disabled={!dirty}
                                                >
                                                    Save
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </PortletHeaderToolbar>
                                }
                            />
                            <PortletBody>
                                <fieldset disabled={!values.isPatientDetailsEnabled} style={{ border: 0, padding: 0, margin: 0 }}>
                                    <div className={classes.container}>
                                        <div className="row" style={{ width: '90%' }}>
                                            <div className="col-md-6">
                                                <FormControl className={classes.formControl} disabled={!values.isPatientDetailsEnabled}>
                                                    <InputLabel htmlFor="providerType">EHR</InputLabel>
                                                    <Select
                                                        required={true}
                                                        name="providerType"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={values.providerType}
                                                        error={Boolean(touched.providerType && errors.providerType)}
                                                        disabled={!values.isPatientDetailsEnabled}
                                                    >
                                                        {FHIRProviderType.map((item, index) => (
                                                            <MenuItem
                                                                key={`${item.id}`}
                                                                value={item.id}
                                                            >
                                                                {item.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <FormHelperText error>{touched.providerType && errors.providerType}</FormHelperText>
                                                </FormControl>
                                            </div>
                                            <div className="col-md-6">
                                                <TextField
                                                    label="Client ID"
                                                    required
                                                    name="providerClientId"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.providerClientId}
                                                    helperText='e.g. AthenaHealth API ClientId'
                                                    error={Boolean(touched.providerClientId && errors.providerClientId && values.isPatientDetailsEnabled)}
                                                    disabled={!values.isPatientDetailsEnabled}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <TextField
                                                    label="Client Secret"
                                                    required
                                                    name="providerClientSecret"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.providerClientSecret}
                                                    helperText='e.g. AthenaHealth API ClientSecret'
                                                    error={Boolean(touched.providerClientSecret && errors.providerClientSecret && values.isPatientDetailsEnabled)}
                                                    disabled={!values.isPatientDetailsEnabled}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <TextField
                                                    label="PracticeId"
                                                    required
                                                    name="practiceId"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.practiceId}
                                                    helperText='e.g. Facility Practice Id'
                                                    error={Boolean(touched.practiceId && errors.practiceId && values.isPatientDetailsEnabled)}
                                                    disabled={!values.isPatientDetailsEnabled}
                                                />
                                            </div>
                                        </div>

                                    </div>
                                </fieldset>
                            </PortletBody>
                        </Portlet>
                    </form>
                )}
            </Formik>
        </Loadable>

        {/* Claim Notes Section  */}
        <Loadable loading={updateClaimNotesPending || createClientClaimNotesPending || getClientClaimNotesPending || testClaimNotesConnectionPending}>
            <Formik
                enableReinitialize={true}
                initialValues={initialValuesClaimNotes}
                validate={values => {
                    const errors: any = {};

                    if (values.providerType != 0 && values.providerType != 1) {
                        errors.providerType = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (!values.practiceId) {
                        errors.practiceId = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (!values.providerClientSecret) {
                        errors.providerClientSecret = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (!values.providerClientId) {
                        errors.providerClientId = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    return errors;
                }}
                onSubmit={(values, { }) => {
                    const payload = {
                        providerType: values?.providerType,
                        practiceId: values?.practiceId,
                        providerClientSecret: values?.providerClientSecret,
                        providerClientId: values?.providerClientId,
                        IsClaimNotesEnabled: values?.IsClaimNotesEnabled,
                        clientId
                    }

                    if (isClaimNotesEdit) {
                        updateClaimNotesConfiguration(payload, handleCallback);
                    } else {
                        createClaimNotesConfiguration(payload, handleCallback);
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    dirty
                }) => (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off" className={classes.container}>
                        <Portlet>
                            <PortletHeader
                                style={{ ...headerBgColor }}
                                toolbar={
                                    <PortletHeaderToolbar className='w-100'>
                                        <Grid container alignItems="center">
                                            <Grid md={6} spacing={2} container alignItems="center" className="pl-3">
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={3}
                                                >
                                                    <Typography className="text-dark" variant="h6">Claim Notes</Typography>
                                                    <Switch
                                                        name="IsClaimNotesEnabled"
                                                        checked={values.IsClaimNotesEnabled}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        color="primary"
                                                    />
                                                </Stack>
                                            </Grid>
                                            <Grid md={6} container className="justify-content-end">
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className='ml-3'
                                                    onClick={() => handleClaimNotesTestConnection(values)}
                                                    disabled={!values.IsClaimNotesEnabled || disabled(values, dirty)}
                                                >
                                                    Test Credentials
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className='ml-3'
                                                    type="submit"
                                                    disabled={!dirty}
                                                >
                                                    Save
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </PortletHeaderToolbar>
                                }
                            />
                            <PortletBody>
                                <fieldset disabled={!values.IsClaimNotesEnabled} style={{ border: 0, padding: 0, margin: 0 }}>
                                    <div className={classes.container}>
                                        <div className="row" style={{ width: '90%' }}>
                                            <div className="col-md-6">
                                                <FormControl className={classes.formControl} disabled={!values.IsClaimNotesEnabled}>
                                                    <InputLabel htmlFor="providerType">EHR</InputLabel>
                                                    <Select
                                                        required={true}
                                                        name="providerType"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={values.providerType}
                                                        error={Boolean(touched.providerType && errors.providerType)}
                                                        disabled={!values.IsClaimNotesEnabled}
                                                    >
                                                        {FHIRProviderType.map((item) => (
                                                            <MenuItem
                                                                key={`${item.id}`}
                                                                value={item.id}
                                                            >
                                                                {item.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <FormHelperText error>{touched.providerType && errors.providerType}</FormHelperText>
                                                </FormControl>
                                            </div>
                                            <div className="col-md-6">
                                                <TextField
                                                    label="Client ID"
                                                    required
                                                    name="providerClientId"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.providerClientId}
                                                    helperText='e.g. AthenaHealth API ClientId'
                                                    error={Boolean(touched.providerClientId && errors.providerClientId && values.IsClaimNotesEnabled)}
                                                    disabled={!values.IsClaimNotesEnabled}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <TextField
                                                    label="Client Secret"
                                                    required
                                                    name="providerClientSecret"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.providerClientSecret}
                                                    helperText='e.g. AthenaHealth API ClientSecret'
                                                    error={Boolean(touched.providerClientSecret && errors.providerClientSecret && values.IsClaimNotesEnabled)}
                                                    disabled={!values.IsClaimNotesEnabled}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <TextField
                                                    label="PracticeId"
                                                    required
                                                    name="practiceId"
                                                    className={classes.textField}
                                                    margin="normal"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.practiceId}
                                                    helperText='e.g. Facility Practice Id'
                                                    error={Boolean(touched.practiceId && errors.practiceId && values.IsClaimNotesEnabled)}
                                                    disabled={!values.IsClaimNotesEnabled}
                                                />
                                            </div>
                                        </div>

                                    </div>
                                </fieldset>
                            </PortletBody>
                        </Portlet>
                    </form>
                )}
            </Formik>
        </Loadable>

        {/* Payer Integrations Section */}
        <Loadable loading={addUpdatePayerIntegrationsPending || getPayerIntegrationConfigurations}>
            <Formik
                enableReinitialize={true}
                initialValues={initialPayerIntegrations || { payerIntegrations: [createEmptyIntegration()] }}
                validate={(values) => validatePayerIntegrations(values, intl, isOptumOrUhc)}
                onSubmit={(values, { }) => {
                    const payload = mapPayerIntegrationsToApi(values.payerIntegrations);
                    AddUpdatePayerIntegrationConfigurations(payload, handleCallback, clientId);
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, initialValues: formikInitialValues }) => {
                    const isPayerIntegrationsDirty = JSON.stringify(values.payerIntegrations) !== JSON.stringify(formikInitialValues.payerIntegrations);
                    return (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off">
                        <Box sx={{ width: '100%' }}>
                            <Portlet>
                                <PortletHeader
                                    style={{ ...headerBgColor }}
                                    toolbar={
                                        <PortletHeaderToolbar className='w-100'>
                                            <Grid container alignItems="center">
                                                <Grid md={6} spacing={2} container alignItems="center" className="pl-3">
                                                    <Typography className="text-dark" variant="h6">Payer Integrations</Typography>
                                                </Grid>
                                                <Grid md={6} container className="justify-content-end" alignItems="center" style={{ gap: 8 }}>
                                                    <Tooltip title="Add payer integration">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setFieldValue('payerIntegrations', [...values.payerIntegrations, createEmptyIntegration()])}
                                                        >
                                                            <AddCircleIcon color="primary" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        className='ml-3'
                                                        type="submit"
                                                        disabled={!isPayerIntegrationsDirty}
                                                    >
                                                        Save
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </PortletHeaderToolbar>
                                    }
                                />
                                <PortletBody>
                                    {(values.payerIntegrations || []).map((integration, index) => {
                                        const showOptum = isOptumOrUhc(integration.payerId);
                                        const integrationErrors = (errors.payerIntegrations?.[index] as any) || {};
                                        const integrationTouched = (touched.payerIntegrations?.[index] as any) || {};
                                        return (
                                            <Box key={integration.uiId} sx={{ borderBottom: '1px solid rgba(0,0,0,0.12)', pb: 3, mb: 1 }}>

                                                {/* Delete button */}
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
                                                    <Tooltip title="Delete integration">
                                                        <IconButton
                                                            size="small"
                                                            className={classes.deleteIconButton}
                                                            onClick={() => setFieldValue('payerIntegrations', values.payerIntegrations.filter((_, i) => i !== index))}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>

                                                <Grid container spacing={2}>
                                                    <Grid item md={6} xs={12}>
                                                        <FormControl
                                                            className={classes.formControl}
                                                            error={Boolean(integrationTouched.payerId && integrationErrors.payerId)}
                                                        >
                                                            <InputLabel>Payer *</InputLabel>
                                                            <Select
                                                                name={`payerIntegrations[${index}].payerId`}
                                                                value={integration.payerId}
                                                                onBlur={handleBlur}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    if (!isOptumOrUhc(e.target.value)) {
                                                                        setFieldValue(`payerIntegrations[${index}].contracts`, []);
                                                                    }
                                                                }}
                                                            >
                                                                {payerOptions.map((p) => (
                                                                    <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            <FormHelperText>{integrationTouched.payerId && integrationErrors.payerId}</FormHelperText>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <Autocomplete
                                                            multiple
                                                            disableCloseOnSelect
                                                            options={payerContractOptions}
                                                            value={(integration.contracts || []).map(c => ({
                                                                id: c.payerContractId,
                                                                displayName:
                                                                    payerContractOptions?.find(p => p.id === c.payerContractId)?.displayName || ""
                                                            }))}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            getOptionLabel={(option) => option.displayName}
                                                            onChange={(_, nextValue) => {
                                                                const existingContracts = integration.contracts || [];
                                                                setFieldValue(
                                                                    `payerIntegrations[${index}].contracts`,
                                                                    nextValue.map(c => {
                                                                    const existing = existingContracts.find(
                                                                        (ec: any) => ec.payerContractId === c.id
                                                                    );
                                                                    return {
                                                                        payerContractId: c.id,
                                                                        payerId: existing?.payerId || ""
                                                                    };
                                                                })
                                                                )
                                                            }}
                                                            renderOption={(props, option, { selected }) => (
                                                                <li {...props}>
                                                                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                                                    {option.displayName}
                                                                </li>
                                                            )}
                                                            renderTags={(tagValue, getTagProps) => {
                                                                const numTags = tagValue.length;
                                                                return (
                                                                    <Typography variant="caption">
                                                                        {tagValue.slice(0, 6).map((option, idx) => (
                                                                            <Chip
                                                                                size="small"
                                                                                color="secondary"
                                                                                key={option.id}
                                                                                label={option.displayName}
                                                                                {...getTagProps({ index: idx })}
                                                                            />
                                                                        ))}
                                                                        {numTags > 6 && ` +${numTags - 6}`}
                                                                    </Typography>
                                                                );
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Payer Contract"
                                                                    required
                                                                    margin="normal"
                                                                    className={classes.textField}
                                                                    // Show error when contracts string-level error exists
                                                                    error={Boolean(integrationTouched.contracts && typeof integrationErrors.contracts === 'string')}
                                                                    helperText={
                                                                        integrationTouched.contracts && typeof integrationErrors.contracts === 'string'
                                                                            ? integrationErrors.contracts
                                                                            : ""
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>

                                                {/* Optum Payer IDs sub-section */}
                                                {showOptum && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                            Optum Payer IDs
                                                        </Typography>
                                                        {(integration.contracts || []).length === 0 ? (
                                                            <Typography variant="body2" color="textSecondary">
                                                                Select payer contract(s) to configure Optum payer IDs.
                                                            </Typography>
                                                        ) : (
                                                            <Grid container spacing={2}>
                                                                {(integration.contracts || []).map((contract, cIdx) => {
                                                                    const entry = integration.contracts?.[cIdx] || {};
                                                                    const contractTouchedEntry = (integrationTouched.contracts?.[cIdx]) || {};
                                                                    const contractErrorEntry = (Array.isArray(integrationErrors.contracts)
                                                                        ? integrationErrors.contracts?.[cIdx]
                                                                        : {}) || {};
                                                                    return (
                                                                        <Grid item md={4} xs={12} key={cIdx}>
                                                                            <TextField
                                                                                label={payerContractOptions?.find(p => p.id === contract.payerContractId)?.displayName || ""}
                                                                                name={`payerIntegrations[${index}].contracts[${cIdx}].payerId`}
                                                                                required
                                                                                className={classes.textField}
                                                                                margin="normal"
                                                                                value={entry.payerId || ''}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                helperText={contractTouchedEntry.payerId && contractErrorEntry.payerId}
                                                                                error={Boolean(contractTouchedEntry.payerId && contractErrorEntry.payerId)}
                                                                            />
                                                                        </Grid>
                                                                    );
                                                                })}
                                                            </Grid>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </PortletBody>
                            </Portlet>
                        </Box>
                    </form>
                )}}
            </Formik>
        </Loadable>
        <AddClientsDialog open={open} handleClose={handleDialogClose} accountDigit={parseInt(initialValuesAccount?.accountIdentifierNumbers)} clientId={clientId} />
        <ConfigurationClearConfirmationDialog open={openConfirmationDialog} handleClose={handleConfirmationDialogClose} handleSubmit={handleDeleteConfiguration} />
    </>);

}

